import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Trash2 } from "lucide-react";
import { ScreenHeader } from "@/components/ScreenHeader";
import { api } from "@/api/client";
import { colors, spacing, fonts } from "@/theme";

function LocationPicker({ selected, onSelect, options, testID }) {
  return (
    <div data-testid={testID} style={pk.row}>
      {options.map((opt) => {
        const active = selected === opt;
        return (
          <button key={opt} type="button" data-testid={`${testID}-${opt}`} onClick={() => onSelect(opt)}
            style={{ ...pk.chip, ...(active ? pk.chipActive : {}) }}>
            <span style={{ ...pk.text, color: active ? colors.onBrandPrimary : colors.onSurface }}>{opt}</span>
          </button>
        );
      })}
    </div>
  );
}

export default function Rates() {
  const navigate = useNavigate();
  const [rates, setRates] = useState([]);
  const [locations, setLocations] = useState([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [usd, setUsd] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    try {
      const [r, l] = await Promise.all([api("/rates"), api("/locations")]);
      setRates(r);
      setLocations(l.locations);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { setLoading(true); load().finally(() => setLoading(false)); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!from || !to || !usd) { setMsg("Fill all fields"); return; }
    const n = parseFloat(usd);
    if (Number.isNaN(n) || n < 0) { setMsg("Invalid rate"); return; }
    setBusy(true); setMsg(null);
    try {
      await api("/rates", { body: { from_location: from, to_location: to, rate_cents: Math.round(n * 100) } });
      setUsd(""); setFrom(""); setTo("");
      setMsg("Rate saved");
      load();
    } catch (e2) { setMsg(e2?.message || "Failed"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api(`/rates/${id}`, { method: "DELETE" }); load(); } catch (e) { console.error(e); }
  };

  return (
    <div style={{ flex: 1, backgroundColor: colors.surface }}>
      <ScreenHeader
        title="Billing Rates"
        subtitle={`${rates.length} configured`}
        right={
          <button data-testid="back-button" onClick={() => navigate("/reports")} style={s.backBtn}>
            <X size={20} color={colors.onSurfaceInverse} />
          </button>
        }
      />
      <div style={s.body}>
        <form onSubmit={save} style={{ maxWidth: 640 }}>
          <div style={s.sectionTitle}>Add / Update rate</div>

          <div style={s.label}>From location</div>
          <LocationPicker testID="rate-from" selected={from} onSelect={setFrom} options={locations} />

          <div style={s.label}>To location</div>
          <LocationPicker testID="rate-to" selected={to} onSelect={setTo} options={locations} />

          <div style={s.label}>Rate (CAD per move)</div>
          <div style={s.usdRow}>
            <span style={s.usdPrefix}>$</span>
            <input data-testid="rate-usd" style={s.usdInput} value={usd} onChange={(e) => setUsd(e.target.value)}
              placeholder="17.00" inputMode="decimal" />
          </div>

          {msg && <div style={s.msg}>{msg}</div>}

          <button type="submit" data-testid="save-rate" disabled={busy} style={{ ...s.cta, ...(busy ? { opacity: 0.6 } : {}) }}>
            <span style={s.ctaText}>{busy ? "…" : "Save rate →"}</span>
          </button>
        </form>

        <div style={s.divider} />
        <div style={s.sectionTitle}>Current rate book</div>

        {loading ? (
          <div style={s.spinnerWrap}><div className="yo-spinner" /></div>
        ) : rates.length === 0 ? (
          <div style={s.empty}>No rates set. Add one above.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: spacing.sm, marginTop: spacing.md, maxWidth: 640 }}>
            {rates.map((r) => (
              <div key={r.id} data-testid={`rate-row-${r.id}`} style={s.row}>
                <div style={{ flex: 1 }}>
                  <div style={s.rowRoute}>{r.from_location} → {r.to_location}</div>
                  <div style={s.rowRate}>${(r.rate_cents / 100).toFixed(2)} CAD / move</div>
                </div>
                <button data-testid={`delete-rate-${r.id}`} onClick={() => remove(r.id)} style={s.delBtn}>
                  <Trash2 size={20} color={colors.error} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const pk = {
  row: { display: "flex", flexWrap: "wrap", gap: 8, padding: "8px 0" },
  chip: { padding: "10px 14px", borderWidth: 2, borderStyle: "solid", borderColor: colors.onSurface, backgroundColor: colors.surfaceSecondary, cursor: "pointer" },
  chipActive: { backgroundColor: colors.brandPrimary, borderColor: colors.brandPrimary },
  text: { ...fonts.monoBold, fontSize: 12, letterSpacing: 0 },
};

const s = {
  body: { padding: spacing.xl },
  backBtn: { borderWidth: 2, borderStyle: "solid", borderColor: colors.onSurfaceInverse, padding: 8, backgroundColor: "transparent", cursor: "pointer", display: "flex" },
  sectionTitle: { ...fonts.display, fontSize: 16, color: colors.onSurface, marginBottom: spacing.sm },
  label: { ...fonts.monoBold, fontSize: 11, color: colors.onSurfaceTertiary, marginTop: spacing.md, marginBottom: spacing.xs },
  usdRow: { display: "flex", borderWidth: 2, borderStyle: "solid", borderColor: colors.onSurface, backgroundColor: colors.surfaceSecondary, alignItems: "center", maxWidth: 320 },
  usdPrefix: { ...fonts.monoBold, fontSize: 18, color: colors.onSurface, padding: `0 ${spacing.md}px` },
  usdInput: { flex: 1, padding: `${spacing.md}px ${spacing.md}px ${spacing.md}px 0`, ...fonts.mono, fontSize: 16, color: colors.onSurface, border: "none", outline: "none", background: "transparent" },
  cta: { marginTop: spacing.lg, backgroundColor: colors.brandPrimary, padding: 16, borderWidth: 2, borderStyle: "solid", borderColor: colors.brandPrimary, cursor: "pointer", display: "block", width: "100%" },
  ctaText: { color: colors.onBrandPrimary, ...fonts.display, fontSize: 14 },
  msg: { marginTop: spacing.sm, ...fonts.monoBold, fontSize: 12, color: colors.brandPrimary },
  divider: { height: 2, backgroundColor: colors.divider, margin: `${spacing.xl}px 0` },
  spinnerWrap: { display: "flex", marginTop: spacing.lg },
  empty: { marginTop: spacing.lg, ...fonts.monoBold, color: colors.onSurfaceTertiary },
  row: { display: "flex", alignItems: "center", gap: spacing.sm, borderWidth: 2, borderStyle: "solid", borderColor: colors.border, backgroundColor: colors.surfaceSecondary, padding: spacing.md },
  rowRoute: { ...fonts.monoBold, fontSize: 14, color: colors.onSurface },
  rowRate: { ...fonts.mono, fontSize: 12, color: colors.success, marginTop: 2 },
  delBtn: { padding: spacing.sm, borderWidth: 2, borderStyle: "solid", borderColor: colors.error, backgroundColor: "transparent", cursor: "pointer", display: "flex" },
};
